import React, { useEffect, useState } from 'react';
import {
  Box, Button, Card, CardContent, CircularProgress, FormControlLabel, Grid,
  IconButton, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow,
  TextField, Typography,
} from '@mui/material';
import { Add as AddIcon, ArrowBack, Delete as DeleteIcon } from '@mui/icons-material';
import { useNavigate, useParams } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { curvaTalleService } from '../../../services/fashionService';
import { CurvaTalle, FashionTalle } from '../../../types/fashion';

const emptyTalle = (orden: number): FashionTalle => ({
  codigoCurva: '',
  codigoTalle: '',
  orden,
});

const CurvaTalleForm: React.FC = () => {
  const { codigoCurva } = useParams();
  const isEdit = Boolean(codigoCurva);
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<CurvaTalle>({
    codigoCurva: '',
    nombreCurva: '',
    estado: 'A',
    talles: [emptyTalle(1)],
  });

  useEffect(() => {
    if (!codigoCurva) return;
    let cancelled = false;
    (async () => {
      try {
        setLoading(true);
        const data = await curvaTalleService.getCurva(codigoCurva);
        if (cancelled) return;
        setForm({
          ...data,
          talles: (data.talles || []).length > 0
            ? data.talles.slice().sort((a, b) => a.orden - b.orden)
            : [emptyTalle(1)],
        });
      } catch {
        if (!cancelled) {
          enqueueSnackbar('No se pudo cargar la curva', { variant: 'error' });
          navigate('/inventario/fashion/curvas-talle');
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [codigoCurva, enqueueSnackbar, navigate]);

  const updateTalle = (idx: number, patch: Partial<FashionTalle>) => {
    setForm((f) => ({
      ...f,
      talles: f.talles.map((t, i) => (i === idx ? { ...t, ...patch } : t)),
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.codigoCurva.trim() || !form.nombreCurva.trim()) {
      enqueueSnackbar('Código y nombre son obligatorios', { variant: 'warning' });
      return;
    }
    const talles = form.talles
      .filter((t) => t.codigoTalle.trim())
      .map((t, i) => ({
        codigoCurva: form.codigoCurva.trim(),
        codigoTalle: t.codigoTalle.trim().toUpperCase(),
        orden: t.orden || i + 1,
      }));
    if (talles.length === 0) {
      enqueueSnackbar('Agregá al menos un talle', { variant: 'warning' });
      return;
    }
    try {
      setSaving(true);
      if (isEdit && codigoCurva) {
        await curvaTalleService.updateCurva(codigoCurva, {
          nombreCurva: form.nombreCurva.trim(),
          estado: form.estado,
          talles,
        });
        enqueueSnackbar('Curva actualizada', { variant: 'success' });
      } else {
        await curvaTalleService.createCurva({
          codigoCurva: form.codigoCurva.trim(),
          nombreCurva: form.nombreCurva.trim(),
          estado: form.estado,
          talles,
        });
        enqueueSnackbar('Curva creada', { variant: 'success' });
      }
      navigate('/inventario/fashion/curvas-talle');
    } catch (err: any) {
      enqueueSnackbar(err?.response?.data || 'Error al guardar', { variant: 'error' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return <Box display="flex" justifyContent="center" p={6}><CircularProgress /></Box>;
  }

  return (
    <Box sx={{ p: { xs: 2, sm: 3 }, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Card sx={{ bgcolor: '#ffffff', borderRadius: 3, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
        <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
          <Stack spacing={2} component="form" onSubmit={handleSubmit}>
            <Stack direction="row" spacing={2} alignItems="center">
              <Button
                startIcon={<ArrowBack />}
                onClick={() => navigate('/inventario/fashion/curvas-talle')}
                sx={{ textTransform: 'none' }}
              >
                Curvas de talle
              </Button>
              <Typography variant="h4" component="h1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                {isEdit ? 'Editar curva de talle' : 'Nueva curva de talle'}
              </Typography>
            </Stack>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <TextField
                  label="Código"
                  fullWidth
                  required
                  disabled={isEdit}
                  value={form.codigoCurva}
                  onChange={(e) => setForm((f) => ({ ...f, codigoCurva: e.target.value.toUpperCase() }))}
                  inputProps={{ maxLength: 20 }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  label="Nombre"
                  fullWidth
                  required
                  value={form.nombreCurva}
                  onChange={(e) => setForm((f) => ({ ...f, nombreCurva: e.target.value }))}
                  inputProps={{ maxLength: 60 }}
                />
              </Grid>
              <Grid item xs={12} md={2}>
                <FormControlLabel
                  control={
                    <Switch
                      checked={form.estado === 'A'}
                      onChange={(e) => setForm((f) => ({ ...f, estado: e.target.checked ? 'A' : 'I' }))}
                    />
                  }
                  label="Activo"
                />
              </Grid>
            </Grid>

            <Stack direction="row" justifyContent="space-between" alignItems="center">
              <Typography variant="h6">Talles (en orden)</Typography>
              <Button
                startIcon={<AddIcon />}
                onClick={() => setForm((f) => ({
                  ...f,
                  talles: [...f.talles, emptyTalle(f.talles.length + 1)],
                }))}
              >
                Agregar talle
              </Button>
            </Stack>

            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell width={100}>Orden</TableCell>
                  <TableCell>Código talle</TableCell>
                  <TableCell width={80} />
                </TableRow>
              </TableHead>
              <TableBody>
                {form.talles.map((t, idx) => (
                  <TableRow key={idx}>
                    <TableCell>
                      <TextField
                        type="number"
                        size="small"
                        value={t.orden}
                        onChange={(e) => updateTalle(idx, { orden: Number(e.target.value) || 0 })}
                      />
                    </TableCell>
                    <TableCell>
                      <TextField
                        size="small"
                        fullWidth
                        value={t.codigoTalle}
                        onChange={(e) => updateTalle(idx, { codigoTalle: e.target.value.toUpperCase() })}
                        inputProps={{ maxLength: 10 }}
                        placeholder="Ej: S, M, 38, 40"
                      />
                    </TableCell>
                    <TableCell>
                      <IconButton
                        size="small"
                        color="error"
                        disabled={form.talles.length <= 1}
                        onClick={() => setForm((f) => ({ ...f, talles: f.talles.filter((_, i) => i !== idx) }))}
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>

            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button variant="outlined" onClick={() => navigate('/inventario/fashion/curvas-talle')}>Cancelar</Button>
              <Button type="submit" variant="contained" disabled={saving}>
                {saving ? <CircularProgress size={22} /> : 'Guardar'}
              </Button>
            </Stack>
          </Stack>
        </CardContent>
      </Card>
    </Box>
  );
};

export default CurvaTalleForm;
